// Storage service for managing application data persistence

import { User } from '../types/user';
import { LoginResponse, SessionSettings, HospitalInfo } from '../types/auth';

class StorageService {
  private static readonly TOKEN_KEY = 'hospital_auth_token';
  private static readonly USER_KEY = 'hospital_user';
  private static readonly REFRESH_TOKEN_KEY = 'hospital_refresh_token';
  private static readonly SETTINGS_KEY = 'hospital_settings';
  private static readonly LAST_VALIDATION_KEY = 'hospital_last_validation';
  private static readonly SESSION_METADATA_KEY = 'hospital_session_metadata';
  private static readonly OFFLINE_MODE_KEY = 'hospital_offline_mode';
  private static readonly SESSION_SETTINGS_KEY = 'hospital_session_settings';
  private static readonly HOSPITAL_KEY = 'hospital_current'; // PHASE 5: Multi-Tenancy
  private static readonly ACCESSIBLE_HOSPITALS_KEY = 'hospital_accessible'; // PHASE 5: Multi-Tenancy

  // Auth related storage
  static setAuthData(loginResponse: LoginResponse, user: User): void {
    // With cookie-based auth, we only store user data
    // The authentication token is handled via httpOnly cookies
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  static getToken(): string | null {
    // With cookie-based auth, we return a placeholder to indicate authentication
    // The actual token is in httpOnly cookies managed by the browser
    const user = this.getUser();
    return user ? 'cookie' : null;
  }

  static getUser(): User | null {
    const userData = localStorage.getItem(this.USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  static clearUserDrafts(): void {
    // Must be called BEFORE clearing auth data (needs user ID)
    const user = this.getUser();
    if (user?.id) {
      const prefix = `draft_${user.id}_`;
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  }

  static clearAuthData(): void {
    const existingUser = this.getUser();
    console.warn(
      `[LOGOUT-DEBUG] StorageService.clearAuthData | reason=auth_data_cleared | ` +
      `user=${existingUser?.username ?? 'null'} | userId=${existingUser?.id ?? 'null'} | ` +
      `timestamp=${new Date().toISOString()}`
    );
    // Clear user drafts before auth data (needs user ID)
    this.clearUserDrafts();
    // Clear localStorage data - httpOnly cookie is cleared by server
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.LAST_VALIDATION_KEY);
    localStorage.removeItem(this.SESSION_METADATA_KEY);
    localStorage.removeItem(this.SESSION_SETTINGS_KEY);
    localStorage.removeItem(this.HOSPITAL_KEY); // PHASE 5
    localStorage.removeItem(this.ACCESSIBLE_HOSPITALS_KEY); // PHASE 5
    // Legacy key used by older useSessionTimeout; keep cleared for consistency
    localStorage.removeItem('loginTime');
  }

  static isTokenExpired(): boolean {
    // With cookie-based auth, expiration is handled by the server
    // Always return false as we can't check cookie expiration from client
    return false;
  }

  // Validation caching methods
  static setLastValidation(timestamp: number): void {
    localStorage.setItem(this.LAST_VALIDATION_KEY, timestamp.toString());
  }

  static getLastValidation(): number | null {
    const timestamp = localStorage.getItem(this.LAST_VALIDATION_KEY);
    return timestamp ? parseInt(timestamp, 10) : null;
  }

  static clearLastValidation(): void {
    localStorage.removeItem(this.LAST_VALIDATION_KEY);
  }

  static shouldRevalidate(): boolean {
    const lastValidation = this.getLastValidation();
    if (!lastValidation) return true;

    const now = Date.now();
    const settings = this.getSessionSettings();
    const intervalMinutes = settings?.session_check_interval_minutes ?? 5;
    const validationCacheTime = Math.max(1, intervalMinutes) * 60 * 1000;

    return (now - lastValidation) > validationCacheTime;
  }

  // Session persistence metadata
  static setSessionMetadata(metadata: { loginTime: number; lastActivity: number; userAgent: string }): void {
    localStorage.setItem(this.SESSION_METADATA_KEY, JSON.stringify(metadata));
  }

  static getSessionMetadata(): { loginTime: number; lastActivity: number; userAgent: string } | null {
    const data = localStorage.getItem(this.SESSION_METADATA_KEY);
    return data ? JSON.parse(data) : null;
  }

  static updateLastActivity(): void {
    const now = Date.now();
    const metadata = this.getSessionMetadata();
    if (metadata) {
      metadata.lastActivity = now;
      this.setSessionMetadata(metadata);
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log('[Storage] Updated last activity timestamp:', new Date(now).toISOString());
      }
    }
  }

  static isSessionValid(): boolean {
    const debug = process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true';
    if (debug) console.log('[StorageService] === isSessionValid() CHECK ===');

    const metadata = this.getSessionMetadata();
    const user = this.getUser();

    if (!metadata || !user) {
      if (debug) console.log('[StorageService] No metadata or user - session INVALID');
      return false;
    }

    const now = Date.now();
    const settings = this.getSessionSettings();

    // --- Absolute login duration check ---
    if (settings?.max_login_duration_enabled) {
      const maxDurationMs = (settings.max_login_duration_minutes ?? 480) * 60 * 1000;
      if (now - metadata.loginTime >= maxDurationMs) {
        if (debug) console.log('[StorageService] Session INVALID - exceeded max login duration');
        return false;
      }
    }

    // --- Idle inactivity check ---
    if (settings?.idle_timeout_enabled) {
      const idleMs = (settings.idle_timeout_minutes ?? 20) * 60 * 1000;
      if (now - metadata.lastActivity >= idleMs) {
        if (debug) console.log('[StorageService] Session INVALID - exceeded idle timeout');
        return false;
      }
    }

    // If both features are disabled, the session is always valid.
    if (debug) console.log('[StorageService] Session VALID');
    return true;
  }

  // Offline mode detection
  static setOfflineMode(offline: boolean): void {
    if (offline) {
      localStorage.setItem(this.OFFLINE_MODE_KEY, 'true');
    } else {
      localStorage.removeItem(this.OFFLINE_MODE_KEY);
    }
  }

  static isOfflineMode(): boolean {
    return localStorage.getItem(this.OFFLINE_MODE_KEY) === 'true';
  }

  // Settings storage
  static setUserSettings(settings: any): void {
    localStorage.setItem(this.SETTINGS_KEY, JSON.stringify(settings));
  }

  static getUserSettings(): any {
    const settings = localStorage.getItem(this.SETTINGS_KEY);
    return settings ? JSON.parse(settings) : {};
  }

  static setSessionSettings(settings: SessionSettings): void {
    localStorage.setItem(this.SESSION_SETTINGS_KEY, JSON.stringify(settings));
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      const idle = settings.idle_timeout_minutes ?? '?';
      const max = settings.max_login_duration_minutes ?? '?';
      const check = settings.session_check_interval_minutes ?? '?';
      console.log(
        `[SESSION] setSessionSettings: idle_timeout_minutes=${idle} max_login_duration_minutes=${max} session_check_interval_minutes=${check}`
      );
    }
  }

  static getSessionSettings(): SessionSettings | null {
    const data = localStorage.getItem(this.SESSION_SETTINGS_KEY);
    return data ? JSON.parse(data) : null;
  }

  // PHASE 5: Multi-Tenancy - Hospital context storage
  static setHospital(hospital: HospitalInfo): void {
    localStorage.setItem(this.HOSPITAL_KEY, JSON.stringify(hospital));
  }

  static getHospital(): HospitalInfo | null {
    const data = localStorage.getItem(this.HOSPITAL_KEY);
    return data ? JSON.parse(data) : null;
  }

  static setAccessibleHospitals(hospitals: HospitalInfo[]): void {
    localStorage.setItem(this.ACCESSIBLE_HOSPITALS_KEY, JSON.stringify(hospitals));
  }

  static getAccessibleHospitals(): HospitalInfo[] {
    const data = localStorage.getItem(this.ACCESSIBLE_HOSPITALS_KEY);
    return data ? JSON.parse(data) : [];
  }
}

export default StorageService;
